import {
    FC,
    ReactElement,
    useEffect,
    useMemo,
    useState,
} from 'react';

import classNames from 'classnames';
import { useLocation } from 'react-router-dom';

import { Picture, Wrapper } from '../../../../components';
import { Navigation, NavigationItem } from '../../../../entities/@navigation/Navigation/Navigation';
import { Image } from '../../../../entities/Media/Media';
import { isSSR } from '../../../../helpers';
import { useTrans } from '../../../../hooks';
import { findMatchingNavigationItem } from '../../helpers';
import { DesktopMainNavigationColumn, SsrNavigation } from '..';

import './DesktopMainNavigationDrawer.scss';

interface DesktopMainNavigationDrawerProps {
    navigationIsOpen: boolean;
    navigationLabel?: string;
    navigation: Navigation;
    onLinkClick: () => void;
    className?: string;
}

export const DesktopMainNavigationDrawer: FC<DesktopMainNavigationDrawerProps> = ({
    navigationIsOpen,
    navigationLabel,
    navigation,
    onLinkClick,
    className = '',
}): ReactElement => {
    const { pathname } = useLocation();
    const trans = useTrans();

    const [subNavigation, setSubNavigation] = useState<Navigation>();
    const [subSubNavigation, setSubSubNavigation] = useState<Navigation>();

    const image = useMemo((): Image | undefined => (
        subSubNavigation?.image || subNavigation?.image || navigation.image
    ), [subNavigation, subSubNavigation]);

    useEffect((): void => {
        if (!navigationIsOpen) {
            return;
        }

        const matchingItem = findMatchingNavigationItem(pathname, navigation);
        const matchingSubNavigationItem = matchingItem?.subNavigation
            ? findMatchingNavigationItem(pathname, matchingItem.subNavigation)
            : undefined;

        setSubNavigation(matchingItem?.subNavigation);
        setSubSubNavigation(matchingSubNavigationItem?.subNavigation);
    }, [navigationIsOpen]);

    const handleLinkClick = (): void => {
        onLinkClick();

        setSubNavigation(undefined);
        setSubSubNavigation(undefined);
    };

    const handleNavigationItemClick = (item: NavigationItem): void => {
        const activeItem = item.id !== subNavigation?.id
            ? item
            : undefined;

        setSubNavigation(activeItem?.subNavigation);
        setSubSubNavigation(undefined);
    };

    const handleSubNavigationItemClick = (item: NavigationItem): void => {
        const activeItem = item.id !== subSubNavigation?.id
            ? item
            : undefined;

        setSubSubNavigation(activeItem?.subNavigation);
    };

    const subNavigationColumnClassNames = classNames('desktop-main-navigation-drawer__column', {
        'desktop-main-navigation-drawer__column--is-expanded': !!subNavigation,
    });

    const subSubNavigationColumnClassNames = classNames('desktop-main-navigation-drawer__column', {
        'desktop-main-navigation-drawer__column--is-expanded': !!subSubNavigation,
    });

    if (isSSR && navigation) {
        return (
            <SsrNavigation navigation={navigation} />
        );
    }

    return (
        <nav
            inert={!navigationIsOpen}
            aria-label={navigationLabel || trans('containers.menu.mainNavigation')}
            className={`desktop-main-navigation-drawer ${className}`}
        >
            <Wrapper className="desktop-main-navigation-drawer__inner-wrapper">
                <DesktopMainNavigationColumn
                    navigation={navigation}
                    activeItemId={subNavigation?.id}
                    onLinkClick={handleLinkClick}
                    onItemClick={handleNavigationItemClick}
                    className="desktop-main-navigation-drawer__column"
                />

                <div className={subNavigationColumnClassNames}>
                    {subNavigation && (
                        <DesktopMainNavigationColumn
                            hasLowerCaseTypeface
                            navigation={subNavigation}
                            activeItemId={subSubNavigation?.id}
                            onLinkClick={handleLinkClick}
                            onItemClick={handleSubNavigationItemClick}
                        />
                    )}
                </div>

                <div className={subSubNavigationColumnClassNames}>
                    {subSubNavigation && (
                        <DesktopMainNavigationColumn
                            hasLowerCaseTypeface
                            navigation={subSubNavigation}
                            onLinkClick={handleLinkClick}
                        />
                    )}
                </div>

                <div className="desktop-main-navigation-drawer__column">
                    {image && (
                        <Picture
                            {...image}
                            className="desktop-main-navigation-drawer__picture"
                            imageClassName="desktop-main-navigation-drawer__image"
                        />
                    )}
                </div>
            </Wrapper>
        </nav>
    );
};
